import { Fragment } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";

import { api } from "@/api/client";
import {
  type CheckResult,
  type QuestionCheck,
  type QuestionCheckEstimate,
  type QuestionList,
  type QuestionListSummary,
  VERDICTS,
} from "@/api/types";
import { PanelHelp } from "@/components/PanelHelp";
import { usePoll } from "@/hooks/usePoll";
import { t, type TranslationKey } from "@/i18n";
import { formatDateTime, formatInt, formatRatio, formatUsdFine, truncateText } from "@/lib/format";
import {
  approxDuration,
  estimateTotals,
  formatCtr,
  formatPosition,
  groupLabel,
  groupSummaries,
  type GroupingMode,
  type GroupView,
  keywordGroups,
  kindLabel,
  MAX_QUESTIONS,
  orderResults,
  progressText,
  qtypeLabel,
  resultErrorLabel,
  type Ticked,
  tickedState,
  typeGroups,
  verdictLabel,
} from "@/lib/questionCheck";
import { downloadCheckCsv } from "@/lib/questionCheckExport";
import { httpUrl } from "@/lib/url";
import { analystCheck, currentView, openInInspector } from "@/state/filters";
import {
  type CheckRun,
  checkRun,
  closeCheck,
  closeList,
  deleteSavedCheck,
  deleteSavedList,
  groupList,
  grouping,
  historyVersion,
  isRunning,
  openList,
  openSavedList,
  pauseCheck,
  questionCheckError,
  resumeCheck,
  type RunPhase,
  selectedRows,
  showCheck,
  STALL_MS,
  startCheck,
  type StopReason,
  tickRows,
} from "@/state/questionCheck";

// «Qualität › Fragen-Check»: paste search queries, group them, check a
// selection through the FAQ as test sessions, and read what came back. The
// workspace and the running check live in `state/questionCheck.ts`, so both
// outlast this view. It reads none of the header's filters.

/** The count the estimate is asked for: its per-question basis is the same
 * for any count, and the totals are `estimateTotals` of the ticked one. */
const ESTIMATE_BASIS_COUNT = 1;
// The backend's bounds on a pasted list (QuestionListCreate).
const MAX_TEXT_CHARS = 500_000;
const MAX_NAME_CHARS = 120;
const ANSWER_PREVIEW_CHARS = 140;

const PHASE_TEXT: Partial<Record<RunPhase, TranslationKey>> = {
  running: "qc.run.running",
  pausing: "qc.run.pausing",
  paused: "qc.run.paused",
  done: "qc.run.done",
};

const STOP_TEXT: Record<StopReason, TranslationKey> = {
  chat_paused: "qc.stop.chat_paused",
  mode_disabled: "qc.stop.mode_disabled",
  storage_failed: "qc.stop.storage_failed",
  network: "qc.stop.network",
  not_found: "qc.stop.not_found",
  failed: "qc.stop.failed",
  stalled: "qc.stop.stalled",
};

const nameOf = (item: { name: string }): string => item.name || t("qc.unnamed");

export function QuestionCheckView() {
  const list = openList.value;
  const run = checkRun.value;
  const check = run.check;
  // Keyed by the list: another list starts with its own grouping, disclosures,
  // estimate and start error.
  return (
    <div class="qc-view">
      <PasteCard />
      {list && <GroupsCard key={list.id} list={list} />}
      {list && <SelectionBar key={list.id} list={list} />}
      {check && <RunCard run={run} check={check} />}
      {check && <ResultsCard run={run} check={check} />}
      <HistoryCard />
    </div>
  );
}

// --- Liste einfügen ----------------------------------------------------------

function PasteCard() {
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const { busy, error } = grouping.value;
  const tooLong = text.length > MAX_TEXT_CHARS;
  const ready = !busy && !tooLong && text.trim() !== "";

  const submit = async (e: Event) => {
    e.preventDefault();
    if (!ready) return;
    const sent = { text, name };
    const title = name.trim();
    if (await groupList({ text, ...(title ? { name: title } : {}) })) {
      // Only what was grouped goes; what was typed meanwhile stays.
      setText((now) => (now === sent.text ? "" : now));
      setName((now) => (now === sent.name ? "" : now));
    }
  };

  return (
    <section class="card" aria-label={t("qc.paste.title")}>
      <h2>{t("qc.paste.title")}</h2>
      <PanelHelp k="qc.overview" />
      <PanelHelp k="qc.paste" />
      <form class="qc-form" onSubmit={(e) => void submit(e)}>
        <label>
          <span>{t("qc.paste.name")}</span>
          <input
            type="text"
            maxLength={MAX_NAME_CHARS}
            value={name}
            onInput={(e) => setName(e.currentTarget.value)}
          />
        </label>
        <label>
          <span>{t("qc.paste.text")}</span>
          <textarea
            rows={8}
            spellcheck={false}
            placeholder={t("qc.paste.placeholder")}
            value={text}
            onInput={(e) => setText(e.currentTarget.value)}
          />
        </label>
        {tooLong && (
          <div class="limit-warn">{t("qc.paste.tooLong", { max: formatInt(MAX_TEXT_CHARS) })}</div>
        )}
        <div class="qc-actions">
          <button type="submit" class="window-pill active" disabled={!ready}>
            {t("qc.paste.group")}
          </button>
          {busy && (
            <span class="kpi-sub" role="status">
              {t("qc.paste.busy")}
            </span>
          )}
        </div>
        {error && (
          <div class="error-banner" role="alert">
            {error}
          </div>
        )}
      </form>
    </section>
  );
}

// --- Gruppen -----------------------------------------------------------------

function GroupsCard({ list }: { list: QuestionList }) {
  const [mode, setMode] = useState<GroupingMode>("keyword");
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const selected = selectedRows.value;
  const views = mode === "keyword" ? keywordGroups(list.groups, list.rows) : typeGroups(list.rows);
  const groupNames = new Map(list.groups.map((group) => [group.key, groupLabel(group)]));
  const { stats } = list;

  const toggle = (key: string) =>
    setExpanded((before) => {
      const next = new Set(before);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <section class="card" aria-label={t("qc.groups.title")}>
      <div class="qc-head">
        <h2>{t("qc.groups.title")}</h2>
        <button type="button" class="window-pill" onClick={closeList}>
          {t("qc.groups.close")}
        </button>
      </div>
      <PanelHelp k="qc.groups" />
      <div class="kpi-sub">
        {t("qc.groups.listMeta", {
          name: nameOf(list),
          date: formatDateTime(list.created_at),
          rows: formatInt(list.row_count),
          groups: formatInt(list.group_count),
        })}
      </div>
      <div class="kpi-sub">
        {t("qc.groups.stats", {
          rules: formatInt(stats.by_rules),
          model: formatInt(stats.by_model),
          unplaced: formatInt(stats.unplaced),
          duplicates: formatInt(stats.duplicates_merged),
        })}
      </div>
      {stats.model_failed && <div class="limit-warn">{t("qc.groups.modelFailed")}</div>}
      <div class="qc-actions qc-switch" role="group" aria-label={t("qc.groups.mode")}>
        {(["keyword", "type"] as const).map((option) => (
          <button
            key={option}
            type="button"
            class={`window-pill ${mode === option ? "active" : ""}`}
            aria-pressed={mode === option}
            onClick={() => setMode(option)}
          >
            {t(option === "keyword" ? "qc.groups.byKeyword" : "qc.groups.byType")}
          </button>
        ))}
      </div>
      <div class="qc-scroll">
        <table class="table qc-groups">
          <thead>
            <tr>
              <th>{t("qc.groups.col.select")}</th>
              <th>{t("qc.groups.col.group")}</th>
              {mode === "keyword" && <th>{t("qc.groups.col.kind")}</th>}
              <th>{t("qc.groups.col.questions")}</th>
              <th>{t("qc.groups.col.clicks")}</th>
              <th>{t("qc.groups.col.impressions")}</th>
              <th>{t("qc.groups.col.ctr")}</th>
              <th>{t("qc.groups.col.position")}</th>
            </tr>
          </thead>
          <tbody>
            {views.map((view) => (
              <GroupRows
                key={`${mode}:${view.key}`}
                view={view}
                mode={mode}
                expanded={expanded.has(view.key)}
                onExpand={() => toggle(view.key)}
                selected={selected}
                groupNames={groupNames}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function GroupRows({
  view,
  mode,
  expanded,
  onExpand,
  selected,
  groupNames,
}: {
  view: GroupView;
  mode: GroupingMode;
  expanded: boolean;
  onExpand: () => void;
  selected: ReadonlySet<number>;
  groupNames: ReadonlyMap<string, string>;
}) {
  const ns = view.rows.map((row) => row.n);
  return (
    <>
      <tr class="qc-group-row" data-testid="qc-group">
        <td>
          <TriStateBox
            state={tickedState(view.rows, selected)}
            label={t("qc.groups.selectGroup", { label: view.label })}
            onTick={(on) => tickRows(ns, on)}
          />
        </td>
        <td>
          <button type="button" class="qc-disclosure" aria-expanded={expanded} onClick={onExpand}>
            <span aria-hidden="true">{expanded ? "▾" : "▸"}</span>{" "}
            <span class="qc-group-label">{view.label}</span>
          </button>
        </td>
        {mode === "keyword" && <td>{view.kind ? kindLabel(view.kind) : ""}</td>}
        <td>{formatInt(view.rows.length)}</td>
        <td>{formatInt(view.clicks)}</td>
        <td>{formatInt(view.impressions)}</td>
        <td>{formatCtr(view.ctr)}</td>
        <td>{formatPosition(view.position)}</td>
      </tr>
      {expanded &&
        view.rows.map((row) => (
          <tr key={row.n} class="qc-question-row">
            <td>
              <input
                type="checkbox"
                checked={selected.has(row.n)}
                aria-label={t("qc.groups.selectQuestion", { query: row.query })}
                onChange={(e) => tickRows([row.n], e.currentTarget.checked)}
              />
            </td>
            <td>
              <span class="qc-query">{row.query}</span>
              {/* Across the other grouping: the type by keyword, the group by type. */}
              <span class="qc-tag">
                {mode === "keyword" ? qtypeLabel(row.qtype) : (groupNames.get(row.group) ?? row.group)}
              </span>
            </td>
            {mode === "keyword" && <td />}
            <td />
            <td>{formatInt(row.clicks)}</td>
            <td>{formatInt(row.impressions)}</td>
            <td>{formatCtr(row.ctr)}</td>
            <td>{formatPosition(row.position)}</td>
          </tr>
        ))}
    </>
  );
}

/** A group's box: ticked when all its questions are, marked when some are. */
function TriStateBox({
  state,
  label,
  onTick,
}: {
  state: Ticked;
  label: string;
  onTick: (on: boolean) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === "some";
  }, [state]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={state === "all"}
      aria-label={label}
      // Partly ticked, it ticks the rest; fully ticked, it unticks all.
      onChange={() => onTick(state !== "all")}
    />
  );
}

// --- Auswahl-Leiste ------------------------------------------------------------

function basisText(estimate: QuestionCheckEstimate): string {
  const search = formatUsdFine(estimate.search_usd_per_query);
  return estimate.assumed
    ? t("qc.bar.basisAssumed", { model: estimate.model, search })
    : t("qc.bar.basis", { turns: formatInt(estimate.basis_turns), model: estimate.model, search });
}

function SelectionBar({ list }: { list: QuestionList }) {
  const selected = selectedRows.value;
  const running = isRunning(checkRun.value);
  const count = selected.size;
  const [basis, setBasis] = useState<QuestionCheckEstimate | null>(null);
  const [failure, setFailure] = useState("");
  // Moves on «Nochmals schätzen»: a failed estimate is asked for again.
  const [attempt, setAttempt] = useState(0);
  const [page, setPage] = useState("");
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState("");
  const wanted = count > 0;
  const max = basis?.max_questions ?? MAX_QUESTIONS;
  const tooMany = count > max;

  // The estimate's basis is the same for every count, and asking for it reads
  // the last 30 days of FAQ turns: it is asked for once per list (the bar is
  // keyed by it), on the first tick, and the totals follow the ticks here.
  useEffect(() => {
    if (!wanted || basis) return;
    const ctl = new AbortController();
    setFailure("");
    api.questionCheckEstimate(ESTIMATE_BASIS_COUNT, ctl.signal).then(
      (next) => {
        if (!ctl.signal.aborted) setBasis(next);
      },
      (err: unknown) => {
        if (!ctl.signal.aborted) setFailure(questionCheckError(err));
      },
    );
    return () => ctl.abort();
  }, [wanted, basis, attempt]);

  const totals = basis && wanted && !tooMany ? estimateTotals(basis, count) : null;
  const pages = basis?.pages ?? [];
  const chosenPage = pages.some((p) => p.origin === page) ? page : (pages[0]?.origin ?? "");
  const pending = wanted && !tooMany && !basis && !failure;
  const canStart = wanted && !tooMany && !pending && !running && !starting;

  const summary = [
    count === 1 ? t("qc.bar.countOne") : t("qc.bar.count", { n: formatInt(count) }),
    ...(totals
      ? [
          t("qc.bar.cost", { cost: formatUsdFine(totals.usd) }),
          t("qc.bar.duration", { d: approxDuration(totals.seconds) }),
        ]
      : []),
  ].join(" · ");

  const start = async () => {
    if (!canStart) return;
    setStarting(true);
    setStartError("");
    try {
      await startCheck(list, [...selected].sort((a, b) => a - b), chosenPage);
    } catch (err) {
      setStartError(questionCheckError(err));
    } finally {
      setStarting(false);
    }
  };

  return (
    <section class="qc-bar" aria-label={t("qc.bar.label")}>
      <div class="qc-bar-main">
        <strong class="qc-bar-summary">{summary}</strong>
        {count === 0 && <span class="kpi-sub">{t("qc.bar.none")}</span>}
        {tooMany && (
          <span class="limit-warn">
            {t("qc.bar.tooMany", { max: formatInt(max), over: formatInt(count - max) })}
          </span>
        )}
        {pending && <span class="kpi-sub">{t("qc.bar.estimating")}</span>}
        {wanted && failure && (
          <>
            <span class="limit-warn">{t("qc.bar.estimateFailed", { error: failure })}</span>
            <button type="button" class="window-pill" onClick={() => setAttempt((a) => a + 1)}>
              {t("qc.bar.estimateRetry")}
            </button>
          </>
        )}
        {pages.length > 0 && (
          <label class="toggle qc-page">
            <span>{t("qc.bar.page")}</span>
            <select value={chosenPage} onInput={(e) => setPage(e.currentTarget.value)}>
              {pages.map((p) => (
                <option key={p.origin} value={p.origin}>
                  {t("qc.bar.pageOption", { origin: p.origin, sessions: formatInt(p.sessions) })}
                </option>
              ))}
            </select>
          </label>
        )}
        <button
          type="button"
          class="window-pill active"
          disabled={!canStart}
          onClick={() => void start()}
        >
          {starting ? t("qc.bar.starting") : t("qc.bar.check")}
        </button>
      </div>
      {basis && (
        <div class="kpi-sub">
          {basisText(basis)} {t("qc.bar.note")}
        </div>
      )}
      {pages.length > 0 && <div class="kpi-sub">{t("qc.bar.pageHint")}</div>}
      {running && <div class="kpi-sub">{t("qc.bar.busy")}</div>}
      {startError && (
        <div class="error-banner" role="alert">
          {startError}
        </div>
      )}
    </section>
  );
}

// --- Prüfung -------------------------------------------------------------------

function RunCard({ run, check }: { run: CheckRun; check: QuestionCheck }) {
  const settled = check.done + check.failed;
  const percent = check.total > 0 ? Math.min(100, (settled / check.total) * 100) : 0;
  const canResume =
    (run.phase === "paused" && run.reason !== "not_found") || run.phase === "pausing";
  // Once a pause is asked for, the check can be put away without waiting for
  // the step in flight, which may take minutes; the backend keeps its results.
  const canClose = run.phase !== "running";
  return (
    <section class="card" aria-label={t("qc.run.title")}>
      <div class="qc-head">
        <h2>{t("qc.run.title")}</h2>
        <div class="qc-actions">
          {run.phase === "running" && (
            <button type="button" class="window-pill" onClick={pauseCheck}>
              {t("qc.run.pause")}
            </button>
          )}
          {canResume && (
            <button type="button" class="window-pill active" onClick={resumeCheck}>
              {t("qc.run.resume")}
            </button>
          )}
          {canClose && (
            <button type="button" class="window-pill" onClick={closeCheck}>
              {t("qc.run.close")}
            </button>
          )}
        </div>
      </div>
      <PanelHelp k="qc.run" />
      <div class="kpi-sub">
        {t("qc.run.meta", {
          name: nameOf(check),
          date: formatDateTime(check.created_at),
          by: check.created_by,
          page: check.page || t("qc.run.noPage"),
        })}
      </div>
      <div
        class="funnel-track qc-progress"
        role="progressbar"
        aria-label={t("qc.run.title")}
        aria-valuemin={0}
        aria-valuemax={check.total}
        aria-valuenow={settled}
        aria-valuetext={progressText(check)}
      >
        <div class="funnel-fill" style={`width: ${percent}%;`} />
      </div>
      <div>
        {progressText(check)} · {t("qc.run.cost", { cost: formatUsdFine(check.cost_usd) })}
      </div>
      <RunMessage run={run} />
    </section>
  );
}

function RunMessage({ run }: { run: CheckRun }) {
  if (run.reason) {
    return (
      <div class="error-banner" role="alert">
        {t(STOP_TEXT[run.reason], {
          error: run.error,
          minutes: formatInt(STALL_MS / 60_000),
        })}
      </div>
    );
  }
  const key = PHASE_TEXT[run.phase];
  return key ? (
    <div class="kpi-sub" role="status">
      {t(key)}
    </div>
  ) : null;
}

// --- Ergebnisse ----------------------------------------------------------------

function ResultsCard({ run, check }: { run: CheckRun; check: QuestionCheck }) {
  const summaries = groupSummaries(run.results, run.groups);
  const names = new Map(summaries.map((summary) => [summary.key, summary.label]));
  const results = orderResults(run.results);
  const empty = results.length === 0;

  const discuss = () => {
    analystCheck.value = {
      id: check.id,
      name: check.name,
      questions: check.total,
      createdAt: check.created_at,
    };
    currentView.value = "analyst";
  };

  return (
    <section class="card" aria-label={t("qc.results.title")}>
      <div class="qc-head">
        <h2>{t("qc.results.title")}</h2>
        <div class="qc-actions">
          <button
            type="button"
            class="window-pill"
            title={t("qc.results.exportTitle")}
            disabled={empty}
            onClick={() => downloadCheckCsv({ check, results: run.results, groups: run.groups })}
          >
            {t("qc.results.export")}
          </button>
          <button type="button" class="window-pill" disabled={empty} onClick={discuss}>
            {t("qc.results.discuss")}
          </button>
        </div>
      </div>
      <PanelHelp k="qc.results" />
      {empty ? (
        <div class="kpi-sub">{t("qc.results.none")}</div>
      ) : (
        <>
          <h3>{t("qc.results.byGroup")}</h3>
          <div class="qc-scroll">
            <table class="table">
              <thead>
                <tr>
                  <th>{t("qc.results.col.group")}</th>
                  <th>{t("qc.results.col.questions")}</th>
                  {VERDICTS.map((verdict) => (
                    <th key={verdict}>{verdictLabel(verdict)}</th>
                  ))}
                  <th>{t("qc.results.col.pages")}</th>
                </tr>
              </thead>
              <tbody>
                {summaries.map((summary) => (
                  <tr key={summary.key} data-testid="qc-summary">
                    <td>{summary.label}</td>
                    <td>{formatInt(summary.questions)}</td>
                    {VERDICTS.map((verdict) => (
                      <td key={verdict}>{formatInt(summary.verdicts[verdict])}</td>
                    ))}
                    <td>{formatInt(summary.pages)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <h3>{t("qc.results.byQuestion")}</h3>
          <div class="qc-scroll">
            <table class="table qc-results">
              <thead>
                <tr>
                  <th>{t("qc.results.col.question")}</th>
                  <th>{t("qc.results.col.verdict")}</th>
                  <th>{t("qc.results.col.bestPage")}</th>
                  <th>{t("qc.results.col.answer")}</th>
                  <th>{t("qc.results.col.cost")}</th>
                  <th>{t("qc.results.col.session")}</th>
                </tr>
              </thead>
              <tbody>
                {results.map((result) => (
                  <ResultRow
                    key={result.n}
                    result={result}
                    group={names.get(result.group) ?? result.group}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function ResultRow({ result, group }: { result: CheckResult; group: string }) {
  return (
    <tr>
      <td class="qc-cell-text">
        <div>{result.query}</div>
        <div class="kpi-sub">
          {group} · {qtypeLabel(result.qtype)}
        </div>
      </td>
      <td>
        <span class={`qc-verdict qc-verdict-${result.verdict}`}>{verdictLabel(result.verdict)}</span>
        {result.listings && <span class="qc-tag">{t("qc.flag.listings")}</span>}
        {result.error && (
          // The code itself stays at hand for the logs.
          <div class="kpi-sub" title={result.error}>
            {resultErrorLabel(result.error)}
          </div>
        )}
      </td>
      <td class="qc-cell-page">
        <BestPage result={result} />
      </td>
      <td class="qc-cell-answer">
        {result.answer ? (
          <details>
            <summary>{truncateText(result.answer, ANSWER_PREVIEW_CHARS)}</summary>
            {/* PLAIN TEXT — never dangerouslySetInnerHTML. */}
            <div class="qc-answer">{result.answer}</div>
          </details>
        ) : (
          <span class="muted">{t("qc.results.noAnswer")}</span>
        )}
      </td>
      <td>{formatUsdFine(result.cost_usd)}</td>
      <td>
        {result.session_id ? (
          <button
            type="button"
            class="link-mono"
            onClick={() => openInInspector(result.session_id)}
          >
            {t("qc.results.inspector")}
          </button>
        ) : (
          "—"
        )}
      </td>
    </tr>
  );
}

function BestPage({ result }: { result: CheckResult }) {
  if (!result.best_url) return <>—</>;
  const href = httpUrl(result.best_url);
  const title = result.best_title || result.best_url;
  return (
    <>
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer">
          {title}
        </a>
      ) : (
        <span>{title}</span>
      )}
      {result.best_score !== null && (
        <div class={result.verdict === "weak" ? "kpi-sub limit-warn" : "kpi-sub"}>
          {t("qc.results.score", { score: formatRatio(result.best_score) })}
        </div>
      )}
    </>
  );
}

// --- Frühere Listen und Prüfungen ----------------------------------------------

type Act = (action: () => Promise<void>) => Promise<void>;

function HistoryCard() {
  const version = historyVersion.value;
  const lists = usePoll((s) => api.questionLists(s), [version]);
  const checks = usePoll((s) => api.questionChecks(s), [version]);
  const run = checkRun.value;
  const active = isRunning(run);
  // "list:<id>" or "check:<id>" while its deletion waits for the confirmation.
  const [confirming, setConfirming] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const loadError = lists.error ?? checks.error;

  const act: Act = async (action) => {
    setBusy(true);
    setError("");
    try {
      await action();
      setConfirming("");
    } catch (err) {
      setError(questionCheckError(err));
      lists.refetch();
      checks.refetch();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section class="card" aria-label={t("qc.history.title")}>
      <h2>{t("qc.history.title")}</h2>
      <PanelHelp k="qc.history" />
      {loadError && <div class="error-banner">{questionCheckError(loadError)}</div>}
      {error && (
        <div class="error-banner" role="alert">
          {error}
        </div>
      )}
      <h3>{t("qc.history.lists")}</h3>
      <SavedLists
        items={lists.data?.lists ?? []}
        busy={busy}
        confirming={confirming}
        onConfirm={setConfirming}
        act={act}
      />
      <h3>{t("qc.history.checks")}</h3>
      {active && <div class="kpi-sub">{t("qc.history.busy")}</div>}
      <SavedChecks
        items={checks.data?.checks ?? []}
        busy={busy}
        active={active}
        loaded={run.check?.id ?? ""}
        confirming={confirming}
        onConfirm={setConfirming}
        act={act}
      />
    </section>
  );
}

function ConfirmRow({
  columns,
  text,
  busy,
  onYes,
  onNo,
}: {
  columns: number;
  text: string;
  busy: boolean;
  onYes: () => void;
  onNo: () => void;
}) {
  return (
    <tr>
      <td colSpan={columns}>
        <div class="hl-orange">{text}</div>
        <div class="qc-actions">
          <button
            type="button"
            class="window-pill ops-danger-button"
            disabled={busy}
            onClick={onYes}
          >
            {t("qc.history.yes")}
          </button>
          <button type="button" class="window-pill" onClick={onNo}>
            {t("qc.history.no")}
          </button>
        </div>
      </td>
    </tr>
  );
}

function SavedLists({
  items,
  busy,
  confirming,
  onConfirm,
  act,
}: {
  items: readonly QuestionListSummary[];
  busy: boolean;
  confirming: string;
  onConfirm: (key: string) => void;
  act: Act;
}) {
  if (items.length === 0) return <div class="kpi-sub">{t("qc.history.noLists")}</div>;
  return (
    <div class="qc-scroll">
      <table class="table">
        <thead>
          <tr>
            <th>{t("qc.history.col.name")}</th>
            <th>{t("qc.history.col.created")}</th>
            <th>{t("qc.history.col.by")}</th>
            <th>{t("qc.history.col.rows")}</th>
            <th>{t("qc.history.col.groups")}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const key = `list:${item.id}`;
            return (
              <Fragment key={item.id}>
                <tr>
                  <td>{nameOf(item)}</td>
                  <td>{formatDateTime(item.created_at)}</td>
                  <td>{item.created_by}</td>
                  <td>{formatInt(item.row_count)}</td>
                  <td>{formatInt(item.group_count)}</td>
                  <td>
                    <div class="qc-actions">
                      <button
                        type="button"
                        class="window-pill"
                        disabled={busy}
                        onClick={() => void act(() => openSavedList(item.id))}
                      >
                        {t("qc.history.open")}
                      </button>
                      <button
                        type="button"
                        class="window-pill"
                        disabled={busy}
                        onClick={() => onConfirm(key)}
                      >
                        {t("qc.history.delete")}
                      </button>
                    </div>
                  </td>
                </tr>
                {confirming === key && (
                  <ConfirmRow
                    columns={6}
                    text={t("qc.history.confirmList", { name: nameOf(item) })}
                    busy={busy}
                    onYes={() => void act(() => deleteSavedList(item.id))}
                    onNo={() => onConfirm("")}
                  />
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SavedChecks({
  items,
  busy,
  active,
  loaded,
  confirming,
  onConfirm,
  act,
}: {
  items: readonly QuestionCheck[];
  busy: boolean;
  /** A check is running: none other opens, and the running one stays. */
  active: boolean;
  loaded: string;
  confirming: string;
  onConfirm: (key: string) => void;
  act: Act;
}) {
  if (items.length === 0) return <div class="kpi-sub">{t("qc.history.noChecks")}</div>;
  return (
    <div class="qc-scroll">
      <table class="table">
        <thead>
          <tr>
            <th>{t("qc.history.col.name")}</th>
            <th>{t("qc.history.col.created")}</th>
            <th>{t("qc.history.col.page")}</th>
            <th>{t("qc.history.col.progress")}</th>
            <th>{t("qc.history.col.cost")}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const key = `check:${item.id}`;
            const locked = active && item.id === loaded;
            return (
              <Fragment key={item.id}>
                <tr>
                  <td>{nameOf(item)}</td>
                  <td>{formatDateTime(item.created_at)}</td>
                  <td>{item.page || "—"}</td>
                  <td>
                    {progressText(item)} ·{" "}
                    {t(item.status === "done" ? "qc.history.status.done" : "qc.history.status.running")}
                  </td>
                  <td>{formatUsdFine(item.cost_usd)}</td>
                  <td>
                    <div class="qc-actions">
                      <button
                        type="button"
                        class="window-pill"
                        disabled={busy || active}
                        onClick={() => void act(() => showCheck(item.id))}
                      >
                        {t("qc.history.open")}
                      </button>
                      <button
                        type="button"
                        class="window-pill"
                        disabled={busy || locked}
                        onClick={() => onConfirm(key)}
                      >
                        {t("qc.history.delete")}
                      </button>
                    </div>
                  </td>
                </tr>
                {confirming === key && (
                  <ConfirmRow
                    columns={6}
                    text={t("qc.history.confirmCheck", {
                      name: nameOf(item),
                      date: formatDateTime(item.created_at),
                    })}
                    busy={busy || locked}
                    onYes={() => void act(() => deleteSavedCheck(item.id))}
                    onNo={() => onConfirm("")}
                  />
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
